import * as THREE from 'three';

/**
 * A chain of capsule segments that trails behind an anchor with spring physics.
 * Used for tail, ears, and any loose bits. Simulated in WORLD space so it reacts
 * to how the anchor actually moves (turns, stops, bounces), not just local animation.
 */
export interface SpringChainOptions {
  segments: number;
  length: number; // total rest length (m)
  radius: number; // base radius (m)
  color: number;
  restDir: THREE.Vector3; // rest direction in the anchor's local space
  stiffness?: number; // spring rate toward rest pose (1/s^2)
  damping?: number; // velocity decay rate (1/s)
  gravity?: number; // m/s^2 pulling the chain down
  taper?: number; // 0..1, how much thinner the tip is than the base
  /** how much the rest pose bends toward world-down along the chain (0 = straight, ~1.5 = folds over) */
  droop?: number;
  /** false: simulate only (a rigged body maps `points` onto bones); no capsule meshes are made */
  render?: boolean;
  /** explicit rest direction per segment in anchor space (overrides restDir + droop) */
  restDirs?: THREE.Vector3[];
}

const _origin = new THREE.Vector3();
const _q = new THREE.Quaternion();
const _rest = new THREE.Vector3();
const _target = new THREE.Vector3();
const _d = new THREE.Vector3();
const _up = new THREE.Vector3(0, 1, 0);
const _restI = new THREE.Vector3();

export class SpringChain {
  readonly meshes: THREE.Mesh[] = [];
  /** simulated joint positions, world space; points[i] is the far end of segment i */
  readonly points: THREE.Vector3[] = [];
  /** world position of the anchor as of the last update */
  readonly origin = new THREE.Vector3();
  private vel: THREE.Vector3[] = [];
  private segLen: number;
  private initialised = false;
  private stiffness: number;
  private damping: number;
  private gravity: number;
  /** Extra world-space force applied this frame (e.g. wag). Reset after each update. */
  force = new THREE.Vector3();

  constructor(
    private anchor: THREE.Object3D,
    scene: THREE.Object3D,
    private opts: SpringChainOptions,
  ) {
    this.segLen = opts.length / opts.segments;
    this.stiffness = opts.stiffness ?? 220;
    this.damping = opts.damping ?? 7;
    this.gravity = opts.gravity ?? 3;
    const render = opts.render ?? true;
    const mat = render ? new THREE.MeshStandardMaterial({ color: opts.color, roughness: 0.85 }) : null;
    for (let i = 0; i < opts.segments; i++) {
      if (mat) {
        const r = opts.radius * (1 - (opts.taper ?? 0.5) * (i / opts.segments));
        const m = new THREE.Mesh(new THREE.CapsuleGeometry(r, this.segLen, 3, 8), mat);
        m.castShadow = true;
        scene.add(m);
        this.meshes.push(m);
      }
      this.points.push(new THREE.Vector3());
      this.vel.push(new THREE.Vector3());
    }
  }

  /** remove the capsule meshes from the scene */
  dispose(): void {
    for (const m of this.meshes) {
      m.removeFromParent();
      m.geometry.dispose();
    }
    this.meshes.length = 0;
  }

  /** Add an impulse (world space). Tip gets more than base, like a flick. */
  kick(impulse: THREE.Vector3): void {
    for (let i = 0; i < this.vel.length; i++) {
      this.vel[i].addScaledVector(impulse, (i + 1) / this.vel.length);
    }
  }

  update(dt: number): void {
    this.anchor.getWorldPosition(_origin);
    this.origin.copy(_origin);
    this.anchor.getWorldQuaternion(_q);
    _rest.copy(this.opts.restDir).normalize().applyQuaternion(_q);
    const restDirs = this.opts.restDirs;

    if (!this.initialised) {
      let prev0 = _origin;
      for (let i = 0; i < this.points.length; i++) {
        _restI.copy(restDirs ? restDirs[i] : _rest);
        if (restDirs) _restI.applyQuaternion(_q);
        this.points[i].copy(prev0).addScaledVector(_restI, this.segLen);
        prev0 = this.points[i];
      }
      this.initialised = true;
    }

    const decay = Math.exp(-this.damping * dt);
    const droop = this.opts.droop ?? 0;
    const n = this.points.length;
    let prev = _origin;
    for (let i = 0; i < n; i++) {
      const p = this.points[i];
      const v = this.vel[i];
      // rest direction bends further toward world-down along the chain
      if (restDirs) _restI.copy(restDirs[i]).applyQuaternion(_q);
      else {
        _restI.copy(_rest);
        if (droop > 0) _restI.addScaledVector(_up, -droop * (n > 1 ? i / (n - 1) : 1)).normalize();
      }
      _target.copy(prev).addScaledVector(_restI, this.segLen);
      _d.subVectors(_target, p);
      v.addScaledVector(_d, this.stiffness * dt);
      v.y -= this.gravity * dt;
      v.addScaledVector(this.force, dt);
      v.multiplyScalar(decay);
      p.addScaledVector(v, dt);
      // keep segment length fixed
      _d.subVectors(p, prev).setLength(this.segLen);
      p.copy(prev).add(_d);
      // orient mesh along the segment
      const m = this.meshes[i];
      if (m) {
        m.position.copy(prev).add(p).multiplyScalar(0.5);
        _d.normalize();
        m.quaternion.setFromUnitVectors(_up, _d);
      }
      prev = p;
    }
    this.force.set(0, 0, 0);
  }
}
